import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Info } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

// Setting keys
const SEO_SETTINGS = {
  META_TITLE: 'seo_meta_title',
  META_DESCRIPTION: 'seo_meta_description',
  GOOGLE_ANALYTICS_ID: 'seo_google_analytics_id',
  GOOGLE_TAG_MANAGER_ID: 'seo_google_tag_manager_id',
  GOOGLE_ADS_CONVERSION_ID: 'seo_google_ads_conversion_id',
  MICROSOFT_UET_ID: 'seo_microsoft_uet_id',
  FACEBOOK_PIXEL_ID: 'seo_facebook_pixel_id',
  KEYWORDS: 'seo_keywords',
  ROBOTS_TXT: 'seo_robots_txt',
  // Stape.io server-side GTM settings
  STAPE_ENABLED: 'stape_enabled',
  STAPE_DOMAIN: 'stape_domain',
  STAPE_SCRIPT_PATH: 'stape_script_path',
  STAPE_DATALAYER_TOKEN: 'stape_datalayer_token',
  // GTM Enhanced Conversions
  GTM_HASH_USER_DATA: 'gtm_hash_user_data',
} as const

// Schema
const seoSchema = z.object({
  meta_title: z.string().max(60, 'Title should be under 60 characters').optional(),
  meta_description: z.string().max(160, 'Description should be under 160 characters').optional(),
  google_analytics_id: z.string().regex(/^(G-[A-Z0-9]+)?$/, 'Must be a valid GA4 ID (G-XXXXXXXX)').optional().or(z.literal('')),
  google_tag_manager_id: z.string().regex(/^(GTM-[A-Z0-9]+)?$/, 'Must be a valid GTM ID (GTM-XXXXXXX)').optional().or(z.literal('')),
  google_ads_conversion_id: z.string().regex(/^(AW-[0-9]+)?$/, 'Must be a valid Google Ads ID (AW-XXXXXXXXXX)').optional().or(z.literal('')),
  microsoft_uet_id: z.string().regex(/^([0-9]+)?$/, 'Must be a numeric UET Tag ID').optional().or(z.literal('')),
  facebook_pixel_id: z.string().optional(),
  keywords: z.string().optional(),
  robots_txt: z.string().optional(),
  // Stape.io server-side GTM fields
  stape_enabled: z.boolean().optional(),
  stape_domain: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  stape_script_path: z.string().regex(/^([a-zA-Z0-9]+\.js)?$/, 'Must be a valid script filename (e.g., abc123.js)').optional().or(z.literal('')),
  stape_datalayer_token: z.string().optional().or(z.literal('')),
  // GTM Enhanced Conversions
  gtm_hash_user_data: z.boolean().optional(),
})

type SEOFormData = z.infer<typeof seoSchema>

export function SEOMarketingPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch account settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Form
  const form = useForm<SEOFormData>({
    resolver: zodResolver(seoSchema),
    defaultValues: {
      meta_title: '',
      meta_description: '',
      google_analytics_id: '',
      google_tag_manager_id: '',
      google_ads_conversion_id: '',
      microsoft_uet_id: '',
      facebook_pixel_id: '',
      keywords: '',
      robots_txt: '',
      // Stape.io defaults
      stape_enabled: false,
      stape_domain: '',
      stape_script_path: '',
      stape_datalayer_token: '',
      // GTM Enhanced Conversions default
      gtm_hash_user_data: false,
    },
  })

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        meta_title: settings[SEO_SETTINGS.META_TITLE] || '',
        meta_description: settings[SEO_SETTINGS.META_DESCRIPTION] || '',
        google_analytics_id: settings[SEO_SETTINGS.GOOGLE_ANALYTICS_ID] || '',
        google_tag_manager_id: settings[SEO_SETTINGS.GOOGLE_TAG_MANAGER_ID] || '',
        google_ads_conversion_id: settings[SEO_SETTINGS.GOOGLE_ADS_CONVERSION_ID] || '',
        microsoft_uet_id: settings[SEO_SETTINGS.MICROSOFT_UET_ID] || '',
        facebook_pixel_id: settings[SEO_SETTINGS.FACEBOOK_PIXEL_ID] || '',
        keywords: settings[SEO_SETTINGS.KEYWORDS] || '',
        robots_txt: settings[SEO_SETTINGS.ROBOTS_TXT] || '',
        // Stape.io settings
        stape_enabled: settings[SEO_SETTINGS.STAPE_ENABLED] === 'true',
        stape_domain: settings[SEO_SETTINGS.STAPE_DOMAIN] || '',
        stape_script_path: settings[SEO_SETTINGS.STAPE_SCRIPT_PATH] || '',
        stape_datalayer_token: settings[SEO_SETTINGS.STAPE_DATALAYER_TOKEN] || '',
        // GTM Enhanced Conversions
        gtm_hash_user_data: settings[SEO_SETTINGS.GTM_HASH_USER_DATA] === 'true',
      })
    }
  }, [settings, form])

  // Mutation
  const mutation = useMutation({
    mutationFn: async (data: SEOFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.META_TITLE,
          value: data.meta_title || null,
          description: 'Default page title for SEO',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.META_DESCRIPTION,
          value: data.meta_description || null,
          description: 'Default meta description for SEO',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.GOOGLE_ANALYTICS_ID,
          value: data.google_analytics_id || null,
          description: 'Google Analytics 4 Measurement ID',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.GOOGLE_TAG_MANAGER_ID,
          value: data.google_tag_manager_id || null,
          description: 'Google Tag Manager Container ID',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.GOOGLE_ADS_CONVERSION_ID,
          value: data.google_ads_conversion_id || null,
          description: 'Google Ads Conversion ID',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.MICROSOFT_UET_ID,
          value: data.microsoft_uet_id || null,
          description: 'Microsoft Ads UET Tag ID',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.FACEBOOK_PIXEL_ID,
          value: data.facebook_pixel_id || null,
          description: 'Facebook Pixel ID',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.KEYWORDS,
          value: data.keywords || null,
          description: 'SEO keywords',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.ROBOTS_TXT,
          value: data.robots_txt || null,
          description: 'Custom robots.txt content',
          is_secret: false,
        },
        // Stape.io server-side GTM settings
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.STAPE_ENABLED,
          value: data.stape_enabled ? 'true' : 'false',
          description: 'Enable Stape.io server-side GTM',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.STAPE_DOMAIN,
          value: data.stape_domain || null,
          description: 'Stape.io proxy domain',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.STAPE_SCRIPT_PATH,
          value: data.stape_script_path || null,
          description: 'Stape.io script path',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.STAPE_DATALAYER_TOKEN,
          value: data.stape_datalayer_token || null,
          description: 'Stape.io dataLayer token',
          is_secret: false,
        },
        // GTM Enhanced Conversions
        {
          account_id: activeAccount.id,
          key: SEO_SETTINGS.GTM_HASH_USER_DATA,
          value: data.gtm_hash_user_data ? 'true' : 'false',
          description: 'Pre-hash user data for Enhanced Conversions',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'SEO settings saved',
        description: 'Your SEO and marketing settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* SEO Settings */}
      <Card>
        <CardHeader>
          <CardTitle>SEO Settings</CardTitle>
          <CardDescription>
            Optimize your website for search engines
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
              className="space-y-6"
            >
              <FormField
                control={form.control}
                name="meta_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Meta Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your Company - Premium Limo Services"
                        maxLength={60}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Default page title for search results ({field.value?.length || 0}/60 characters)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="meta_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Meta Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Experience luxury transportation with our premium fleet of vehicles..."
                        maxLength={160}
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Default description for search results ({field.value?.length || 0}/160 characters)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="keywords"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Keywords</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="limo service, luxury transportation, airport transfer, wedding limo..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Comma-separated keywords for your business
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="border-t pt-6">
                <h3 className="mb-4 text-lg font-medium">Analytics & Tracking</h3>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="google_analytics_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Google Analytics 4 ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="G-XXXXXXXXXX"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Your GA4 Measurement ID (found in Admin &gt; Data Streams)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="google_tag_manager_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Google Tag Manager ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="GTM-XXXXXXX"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Your GTM Container ID
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="google_ads_conversion_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Google Ads Conversion ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="AW-XXXXXXXXXX"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Your Google Ads Conversion ID (found in Google Ads &gt; Tools &gt; Conversions)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="microsoft_uet_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Microsoft Ads UET Tag ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="12345678"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Your Microsoft Ads UET Tag ID (found in Microsoft Ads &gt; Conversion Tracking &gt; UET Tags)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="facebook_pixel_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Facebook Pixel ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="1234567890123456"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Your Facebook/Meta Pixel ID for conversion tracking
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Stape.io Server-Side GTM Section */}
                <div className="mt-6 rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium">Server-Side GTM (Stape.io)</h4>
                      <p className="text-sm text-muted-foreground">
                        Enable server-side tracking for improved accuracy and privacy compliance
                      </p>
                    </div>
                    <FormField
                      control={form.control}
                      name="stape_enabled"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {form.watch('stape_enabled') && (
                    <div className="mt-4 space-y-4">
                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          When enabled, Stape.io settings override the standard GTM container.
                          The GTM Container ID above is still required for the noscript fallback.
                        </AlertDescription>
                      </Alert>

                      <FormField
                        control={form.control}
                        name="stape_domain"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Stape.io Domain</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="https://load.yourdomain.com"
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              Your Stape.io proxy domain (e.g., https://load.go.limos.ai)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="stape_script_path"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Script Path</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="abc123xyz.js"
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              The obfuscated script filename from your Stape.io container
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="stape_datalayer_token"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>DataLayer Token</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="7gefq19l=EBFaLi0r..."
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              The encrypted container identifier from your Stape.io setup
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </div>

                {/* Enhanced Conversions User Data Hashing Section */}
                <div className="mt-6 rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium">Pre-hash User Data</h4>
                      <p className="text-sm text-muted-foreground">
                        Hash user data (email, phone, name) before sending to Google for Enhanced Conversions
                      </p>
                    </div>
                    <FormField
                      control={form.control}
                      name="gtm_hash_user_data"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {form.watch('gtm_hash_user_data') && (
                    <Alert className="mt-4">
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        When enabled, user data is normalized and SHA-256 hashed before being sent to the dataLayer.
                        This is required when using Google Ads Enhanced Conversions with pre-hashed data.
                        Ensure your GTM container is configured to read sha256_ prefixed fields.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="mb-4 text-lg font-medium">Advanced</h3>

                <FormField
                  control={form.control}
                  name="robots_txt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Custom robots.txt</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={`User-agent: *\nAllow: /\n\nSitemap: https://yourdomain.com/sitemap.xml`}
                          className="min-h-[120px] font-mono text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Custom robots.txt content for search engine crawlers
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
